import { useMemo, useState } from "react";
import { useLanguageContext } from "@/hooks";
import { Error, Success } from "@/_service/notification/notification";
import { uploadMediaFile, deleteMediaFile } from "@/apis";
import { fileSizeInMB } from "@/utility";
import { MediaType } from "../../../types/media.types";

export type MediaItem = {
  id?: string | number;
  type: MediaType;
  url?: string;
  file?: File;
  name?: string;
};

type MediaUploaderProps = {
  initialMedia?: MediaItem[];
  maxImages?: number;
  maxVideos?: number;
  maxImageSizeMb?: number;
  maxVideoSizeMb?: number;
  allowYoutube?: boolean;
  allowImage?: boolean;
  allowVideo?: boolean;
  uploadStrategy?: "upload" | "local"; // upload => calls uploadFile or provided uploader | local => returns File objects
  assetId?: string | number;
  assetType?: string;
  mediaType?: MediaType;
  onChange?: (items: MediaItem[]) => void;
  onUpload?: (file: File, type: MediaType) => Promise<string>; // returns url
};

const defaultProps = {
  maxImages: 4,
  maxVideos: 1,
  maxImageSizeMb: 2,
  maxVideoSizeMb: 5,
  allowYoutube: true,
  allowImage: true,
  allowVideo: true,
  uploadStrategy: "upload" as const,
};

export default function MediaUploader(props: MediaUploaderProps) {
  const {
    initialMedia = [],
    maxImages = defaultProps.maxImages,
    maxVideos = defaultProps.maxVideos,
    maxImageSizeMb = defaultProps.maxImageSizeMb,
    maxVideoSizeMb = defaultProps.maxVideoSizeMb,
    allowYoutube = defaultProps.allowYoutube,
    allowImage = defaultProps.allowImage,
    allowVideo = defaultProps.allowVideo,
    uploadStrategy = defaultProps.uploadStrategy,
    assetId = 2,
    assetType = "NA_WEB",
    mediaType = "NA_WEB",
    onChange,
    onUpload,
  } = props;

  const { language } = useLanguageContext();
  const [mediaItems, setMediaItems] = useState<MediaItem[]>(initialMedia);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const imageCount = useMemo(
    () => mediaItems.filter((m) => m.type === "IMAGE").length,
    [mediaItems]
  );
  const videoCount = useMemo(
    () => mediaItems.filter((m) => m.type === "VIDEO").length,
    [mediaItems]
  );

  const notifyChange = (items: MediaItem[]) => {
    setMediaItems(items);
    onChange?.(items);
  };

  const validateFile = (file: File, type: MediaType): boolean => {
    if (type === "IMAGE") {
      const size = Number(fileSizeInMB(file.size));
      if (size > maxImageSizeMb) {
        Error(
          language("msg.image_size_limit") ||
            `Image size cannot be more than ${maxImageSizeMb}MB`
        );
        return false;
      }
      if (!file.type.startsWith("image/")) {
        Error(language("msg.invalid_image") || "Only image files are allowed");
        return false;
      }
    }
    if (type === "VIDEO") {
      const size = Number(fileSizeInMB(file.size));
      if (size > maxVideoSizeMb) {
        Error(
          language("msg.video_size_limit") ||
            `Video size cannot be more than ${maxVideoSizeMb}MB`
        );
        return false;
      }
      if (!file.type.startsWith("video/")) {
        Error(language("msg.invalid_video") || "Only video files are allowed");
        return false;
      }
    }
    return true;
  };

  const performUpload = async (
    file: File,
    type: MediaType
  ): Promise<{ url: string; id?: string | number }> => {
    if (onUpload) {
      const uploadedUrl = await onUpload(file, type);
      return { url: uploadedUrl };
    }

    const form = new FormData();
    form.set("file", file);
    form.set("assetId", `${assetId}`);
    form.set("mediaType", type);
    form.set("assetType", assetType);
    form.set("metadata", "");

    const res = await uploadMediaFile(form);
    const url = res?.data?.response?.url;
    const id = res?.data?.response?.id;
    if (!url) {
      throw Error("Upload did not return url");
    }
    return { url, id };
  };

  const addMediaItems = async (files: FileList | null, type: MediaType) => {
    if (!files || files.length === 0) return;

    const newItems: MediaItem[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!validateFile(file, type)) continue;
      newItems.push({ type, file, name: file.name });
    }

    const current = [...mediaItems];

    if (type === "IMAGE") {
      if (imageCount + newItems.length > maxImages) {
        Error(
          language("msg.image_count_limit") ||
            `You can upload maximum ${maxImages} images`
        );
        return;
      }
    }
    if (type === "VIDEO") {
      if (videoCount + newItems.length > maxVideos) {
        Error(
          language("msg.video_count_limit") ||
            `You can upload maximum ${maxVideos} videos`
        );
        return;
      }
    }

    if (uploadStrategy === "upload") {
      setIsUploading(true);
      try {
        const uploadedItems: MediaItem[] = [];
        for (const item of newItems) {
          const { url, id } = await performUpload(item.file as File, type);
          uploadedItems.push({ ...item, url, id, file: undefined });
        }
        notifyChange([...current, ...uploadedItems]);
        Success(language("msg.image_uploaded") || "Uploaded successfully");
      } catch (err) {
        console.error(err);
        Error(language("msg.failed_to_upload_image") || "Failed to upload");
      } finally {
        setIsUploading(false);
      }
    } else {
      // local mode returns File objects
      notifyChange([...current, ...newItems]);
    }
  };

  const handleYoutubeAdd = () => {
    if (!youtubeUrl) return;
    const current = [...mediaItems.filter((m) => m.type !== "YOUTUBE_VIDEO")];
    current.push({ type: MediaType.YOUTUBE_VIDEO, url: youtubeUrl });
    notifyChange(current);
    setYoutubeUrl("");
  };

  const removeItem = async (index: number) => {
    const target = mediaItems[index];
    if (!target) return;

    // Call delete API when in upload mode and backend id exists
    if (uploadStrategy === "upload" && target.id) {
      try {
        await deleteMediaFile(target.id);
      } catch (error) {
        console.error(error);
        Error(language("msg.delete_failed") || "Failed to delete media");
        return;
      }
    }

    const next = mediaItems.filter((_, idx) => idx !== index);
    notifyChange(next);
  };

  const renderMediaCard = (item: MediaItem, index: number) => {
    const isImage = item.type === "IMAGE";
    const isVideo = item.type === "VIDEO";
    const src =
      item.url ||
      (item.file ? URL.createObjectURL(item.file) : undefined) ||
      undefined;

    return (
      <div
        key={index}
        className="position-relative border rounded p-2 d-flex align-items-center justify-content-center"
        style={{ minWidth: 110, minHeight: 110 }}
      >
        <button
          type="button"
          className="btn btn-sm btn-light position-absolute top-0 end-0 m-1"
          onClick={() => removeItem(index)}
        >
          <i className="bi bi-x-lg" />
        </button>
        {isImage && src && (
          <img
            src={src}
            alt={item.name || "image"}
            className="img-fluid rounded"
            style={{ maxHeight: 96 }}
          />
        )}
        {isVideo && src && (
          <video width="96" height="96" controls className="rounded">
            <source src={src} />
          </video>
        )}
        {item.type === "YOUTUBE_VIDEO" && (
          <div className="text-center">
            <i className="bi bi-youtube text-danger fs-3 d-block" />
            <small className="text-break">{item.url}</small>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <div className="text-muted">
          {language("label.add_max_4_images_in_jpeg_format")?.replace('4', maxImages) ||
            `Add max ${maxImages} images with ${maxImageSizeMb} mb size jpeg or png format`}
        </div>
        <div className="d-flex gap-2 flex-wrap mt-2">
          {mediaItems.filter((m) => m.type === "IMAGE").map(renderMediaCard)}
          {allowImage && imageCount < maxImages && (
            <label
              className="border rounded d-flex align-items-center justify-content-center"
              style={{ width: 110, height: 110, cursor: "pointer" }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="d-none"
                onChange={(e) => addMediaItems(e.target.files, MediaType.IMAGE)}
              />
              {isUploading ? (
                <span className="spinner-border spinner-border-sm"></span>
              ) : (
                <i className="bi bi-plus fs-2 text-muted" />
              )}
            </label>
          )}
        </div>
      </div>

      {(allowYoutube || allowVideo) && (
        <div className="p-3 rounded-3 bg-light">
          {allowYoutube && (
            <>
              <div className="d-flex align-items-center gap-2 mb-2">
                <label className="form-label mb-0">
                  {language("label.youtube_url") || "Please share your youtube URL"}
                </label>
                <i className="bi bi-youtube text-danger fs-5"></i>
              </div>
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://www.youtube.com/"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                />
                <button className="btn btn-outline-secondary" type="button" onClick={handleYoutubeAdd}>
                  <i className="bi bi-save" />
                </button>
              </div>
            </>
          )}

          {allowYoutube && allowVideo && (
            <div className="d-flex align-items-center my-2">
              <div className="flex-grow-1 border-top"></div>
              <div className="px-3 text-muted">
                {language("label.or") || "OR"}
              </div>
              <div className="flex-grow-1 border-top"></div>
            </div>
          )}

          {allowVideo && (
            <>
              <div className="text-muted mb-2">
                {language("label.add_video_limit") ||
                  `Add one video in mp4 format with less than ${maxVideoSizeMb} mb`}
              </div>
              <div className="d-flex gap-2 flex-wrap mt-2">
                {mediaItems.filter((m) => m.type === "VIDEO").map(renderMediaCard)}
                {videoCount < maxVideos && (
                  <label
                    className="border rounded d-flex align-items-center justify-content-center"
                    style={{ width: 110, height: 110, cursor: "pointer" }}
                  >
                    <input
                      type="file"
                      accept="video/*"
                      className="d-none"
                      onChange={(e) => addMediaItems(e.target.files, MediaType.VIDEO)}
                    />
                    {isUploading ? (
                      <span className="spinner-border spinner-border-sm"></span>
                    ) : (
                      <i className="bi bi-plus fs-2 text-muted" />
                    )}
                  </label>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {mediaItems.some((m) => m.type === "YOUTUBE_VIDEO") && (
        <div className="d-flex gap-2 flex-wrap">
          {mediaItems
            .map((m, idx) => ({ item: m, idx }))
            .filter(({ item }) => item.type === "YOUTUBE_VIDEO")
            .map(({ item, idx }) => renderMediaCard(item, idx))}
        </div>
      )}
    </div>
  );
}
